import React, { useEffect, useState } from "react"
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel
} from "@mui/material"
import styled from "@emotion/styled"
import { Formik } from "formik"
import { Col, Row } from "react-bootstrap"
import { useTheme } from "@mui/system"
import { useSelector, useDispatch } from "react-redux"
import CloseIcon from "@mui/icons-material/Close"
import {
  createLeagueAction,
  createLeagueBulkAction,
  updateLeagueAction,
  getAvailableRestaurantsAction,
  getLeagueDataAction,
  uploadLeaguePrizeImageAction,
  getEligibleUsersCountAction
} from "redux/DMV/actions"
import { DMVActions } from "redux/DMV/slice/user"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppDatePicker from "components/AppDatePicker"
import MultiSelect from "components/StyledComponents/MultipleSelect"
import StyledLabel from "components/StyledComponents/StyledLabel"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import ImageCropper from "components/ImageCropper"
import { validationSchemaLeague } from "helpers/validationSchema"
import { convertDate, payloadDate } from "helpers/functions"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent",
    maxWidth: 850,
    width: "100%"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: "100%",
    padding: 32,
    boxSizing: "border-box"
  }
}))

const AddNewLeagueForm = ({ open, onClose, isEdit = false }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const {
    leagueId,
    leagueData,
    availableRestaurants,
    prizeImage,
    occupiedRestaurants,
    eligibleUsersCount
  } = useSelector((store) => store?.dmv?.league || {})
  const [restaurantsList, setRestaurantsList] = useState([])
  const [datesSelected, setDatesSelected] = useState(false)
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }

  useEffect(() => {
    if (open && isEdit) {
      dispatch(getLeagueDataAction({ leagueId: leagueId }))
      setDatesSelected(true)
    }
  }, [open])

  useEffect(() => {
    if (isEdit && leagueData) {
      dispatch(
        getAvailableRestaurantsAction({
          startDate: leagueData.start_date,
          endDate: leagueData.end_date,
          leagueId: leagueId
        })
      )
    }
  }, [leagueData])

  useEffect(() => {
    if (availableRestaurants.length > 0 || (isEdit && occupiedRestaurants?.length > 0)) {
      let allRestaurants = [...availableRestaurants]

      // In edit mode, add back current league's restaurants from occupied restaurants
      if (isEdit && occupiedRestaurants?.length > 0 && leagueData?.restaurant_ids?.length > 0) {
        const currentLeagueRestaurantIds = leagueData.restaurant_ids

        // Keep only restaurants that ARE in the current league from occupied restaurants
        const currentLeagueRestaurants = occupiedRestaurants.filter((restaurant) =>
          currentLeagueRestaurantIds.includes(restaurant.id)
        )

        // Merge available and current league's restaurants
        allRestaurants = [...availableRestaurants, ...currentLeagueRestaurants]

        // Remove duplicates based on id
        const uniqueRestaurants = {}
        allRestaurants.forEach((restaurant) => {
          uniqueRestaurants[restaurant.id] = restaurant
        })
        allRestaurants = Object.values(uniqueRestaurants)
      }

      const allKeyPair = [
        { value: "ALL", label: "All" },
        ...allRestaurants.map((res) => ({
          value: res.id,
          label: res.name
        }))
      ]
      setRestaurantsList(allKeyPair)
      setDatesSelected(true)
    } else {
      setRestaurantsList([])
    }
  }, [availableRestaurants, occupiedRestaurants])

  useEffect(() => {
    if (isEdit && leagueData?.restaurant_ids?.length > 0 && restaurantsList?.length > 0) {
      dispatch(
        getEligibleUsersCountAction({
          restaurant_ids: leagueData.restaurant_ids
        })
      )
    }
  }, [isEdit, leagueData?.restaurant_ids, restaurantsList?.length])

  const getInitialValues = () => {
    if (isEdit) {
      const restaurantIds = leagueData.restaurant_ids || []
      return {
        contestName: leagueData.name || "",
        restaurants: restaurantIds,
        startDate: leagueData.start_date ? new Date(leagueData.start_date) : null,
        endDate: leagueData.end_date ? new Date(leagueData.end_date) : null,
        numberOfPods: leagueData.total_pods ? String(leagueData.total_pods) : "",
        prizeTitle: leagueData.prize_text || "",
        prizeImage: { id: leagueData.prize_image, url: leagueData.prize_image_url } || null,
        separatePodsPerRestaurant: leagueData.is_mixed_restaurants === true
      }
    }

    return {
      contestName: "",
      restaurants: [],
      startDate: null,
      endDate: null,
      numberOfPods: "",
      prizeTitle: "",
      prizeImage: null,
      separatePodsPerRestaurant: false
    }
  }

  const handleSubmit = async (values) => {
    let restaurantIds = []

    if (values?.restaurants?.includes("ALL")) {
      restaurantIds = availableRestaurants?.map((res) => res.id)
    } else {
      restaurantIds = values.restaurants
    }
    const { startDate, endDate } = values
    const currentTime = new Date().toISOString().split("T")[1].split(".")[0]
    const convertedStartDate = convertDate(startDate, currentTime).split(" ")
    const convertedEndDate = convertDate(endDate, currentTime).split(" ")
    const payloadStartDate = payloadDate(convertedStartDate)
    const payloadEndDate = payloadDate(convertedEndDate)
    let payload = {
      name: values.contestName,
      start_date: payloadStartDate,
      end_date: payloadEndDate,
      restaurant_ids: restaurantIds,
      number_of_pods: Number(values?.numberOfPods),

      prize_text: values?.prizeTitle,
      prize_image_id: prizeImage ? prizeImage : values?.prizeImage?.id || null
    }

    if (isEdit) {
      payload.mix_restaurants = !values?.separatePodsPerRestaurant
      dispatch(updateLeagueAction({ leagueId, payload }))
    } else {
      if (values?.separatePodsPerRestaurant) {
        payload.mix_restaurants = false
        dispatch(createLeagueBulkAction({ payload }))
      } else {
        payload.mix_restaurants = true
        dispatch(createLeagueAction({ payload }))
      }
    }
  }

  useEffect(() => {
    if (!open) {
      setDatesSelected(false)
      setRestaurantsList([])
      setCrop(false)
      setFileDetails(null)
      setImageObjectUrl(null)
      dispatch(DMVActions.setLeaguePrizeImage(null))
      dispatch(DMVActions.setLeagueData({}))
      dispatch(DMVActions.setEligibleUsersCount(0))
    } else {
      dispatch(DMVActions.setEligibleUsersCount(0))
    }
  }, [open])

  const handleRestaurantChange = (e, formik) => {
    const selectedRestaurants = e.target.value

    if (selectedRestaurants.includes("ALL")) {
      formik.setFieldValue("restaurants", ["ALL"])
      // Get all restaurant IDs when "ALL" is selected
      const allRestaurantIds = availableRestaurants.map((res) => res.id)
      dispatch(getEligibleUsersCountAction({ restaurant_ids: allRestaurantIds }))
    } else {
      formik.setFieldValue("restaurants", selectedRestaurants)
      if (selectedRestaurants.length > 0) {
        dispatch(getEligibleUsersCountAction({ restaurant_ids: selectedRestaurants }))
      } else {
        dispatch(DMVActions.setEligibleUsersCount(0))
      }
    }
  }

  const handleDateChange = (field, value, formik) => {
    formik.setFieldValue(field, value)

    const startDate = field === "startDate" ? value : formik.values.startDate
    const endDate = field === "endDate" ? value : formik.values.endDate

    if (startDate && endDate) {
      formik.setFieldValue("restaurants", [])
      setDatesSelected(false)
      dispatch(DMVActions.setEligibleUsersCount(0))

      const currentTime = new Date().toISOString().split("T")[1].split(".")[0]
      const convertedStartDate = convertDate(startDate, currentTime).split(" ")
      const convertedEndDate = convertDate(endDate, currentTime).split(" ")
      const formattedStartDate = payloadDate(convertedStartDate)
      const formattedEndDate = payloadDate(convertedEndDate)

      const payload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate
      }

      // Include leagueId when editing
      if (isEdit && leagueId) {
        payload.leagueId = leagueId
      }

      dispatch(getAvailableRestaurantsAction(payload))
    } else {
      setDatesSelected(false)
      formik.setFieldValue("restaurants", [])
      dispatch(DMVActions.setEligibleUsersCount(0))
    }
  }

  const shouldDisableStartDate = (date, endDate) => {
    // If end date is selected, start date must be before or equal to end date
    if (endDate && new Date(date) > new Date(endDate)) {
      return true
    }
    return false
  }

  const shouldDisableEndDate = (date, startDate) => {
    if (startDate && new Date(date) <= new Date(startDate)) {
      return true
    }
    return false
  }

  const onImageUpload = (image) => {
    if (image) {
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  const handleImageDelete = (formik) => {
    setCrop(false)
    setFileDetails(null)
    setImageObjectUrl(null)
    dispatch(DMVActions.setLeaguePrizeImage(null))
    formik.setFieldValue("prizeImage", null)
  }

  function handleCroppedImage(image, formik) {
    let data = new FormData()
    data.append("media", image)
    data.append("media_type", "IMAGE")
    data.append("content_type", image.type)
    dispatch(uploadLeaguePrizeImageAction(data))
    formik.setFieldValue("prizeImage", image)
    formik.setFieldTouched("prizeImage")
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={16 / 9}
        onClose={setCrop}
        openCrop={openCrop}
      />
    )
  }

  return (
    <Formik
      key={isEdit ? leagueId : open ? "create" : "closed"}
      initialValues={getInitialValues()}
      validationSchema={validationSchemaLeague}
      onSubmit={handleSubmit}
      enableReinitialize={true}>
      {(formik) => (
        <StyledDialog open={open}>
          <DialogContent>
            {showCropperModal(imageObjectURL, fileDetails, formik)}
            <Box className="d-flex justify-content-end">
              <IconButton onClick={() => onClose()} sx={styles.closeBtn}>
                <CloseIcon sx={{ height: "14px", width: "14px" }} />
              </IconButton>
            </Box>
            <Box className="w-100 d-flex justify-content-center mb-2">
              <Typography variant="h2">{isEdit ? "Edit League" : "Create New League"}</Typography>
            </Box>

            <Box flexDirection="column" className="mt-4">
              <Box className="w-100 py-2">
                <Row className="w-100 m-0">
                  <Col lg="12">
                    <AppTextField
                      id="contestName"
                      name="contestName"
                      isUpperCase={true}
                      type="text"
                      fullWidth
                      label="Contest Name"
                      isRequired={true}
                      placeholder="Enter Contest Name"
                      value={formik.values.contestName}
                      onChange={formik.handleChange}
                      error={formik.touched.contestName && Boolean(formik.errors.contestName)}
                      helperText={formik.touched.contestName && formik.errors.contestName}
                    />
                  </Col>
                </Row>

                <Row className="w-100 m-0 mt-4">
                  <Col lg="6">
                    <Grid item className="datesContainer">
                      <StyledLabel variant="body1Regular">
                        Contest Start Date
                        <span>*</span>
                      </StyledLabel>
                      <Box className="dateFieldContainer w-100 mt-2">
                        <AppDatePicker
                          inputFormat="dd MMM yyyy"
                          disableMaskedInput
                          placeholder="Start Date"
                          shouldDisableDate={(date) =>
                            shouldDisableStartDate(date, formik.values.endDate)
                          }
                          value={formik.values.startDate}
                          onChange={(val) => handleDateChange("startDate", val, formik)}
                          error={formik.errors.startDate}
                          touched={formik.touched.startDate}
                        />
                      </Box>
                    </Grid>
                  </Col>
                  <Col lg="6">
                    <Grid item className="datesContainer">
                      <StyledLabel variant="body1Regular">
                        Contest End Date
                        <span>*</span>
                      </StyledLabel>
                      <Box className="dateFieldContainer w-100 mt-2">
                        <AppDatePicker
                          inputFormat="dd MMM yyyy"
                          disableMaskedInput
                          placeholder="End Date"
                          shouldDisableDate={(date) =>
                            shouldDisableEndDate(date, formik.values.startDate)
                          }
                          value={formik.values.endDate}
                          onChange={(val) => handleDateChange("endDate", val, formik)}
                          error={formik.errors.endDate}
                          touched={formik.touched.endDate}
                        />
                      </Box>
                    </Grid>
                  </Col>
                </Row>

                <Row className="w-100 m-0 mt-4">
                  <Col lg="12">
                    <MultiSelect
                      id="restaurants"
                      name="restaurants"
                      menuItems={restaurantsList}
                      label="Select Restaurant(s)"
                      placeholder={
                        datesSelected
                          ? "Select Restaurants"
                          : "Please select start and end dates first"
                      }
                      isRequired={true}
                      search={true}
                      checkBox={true}
                      disabled={!datesSelected}
                      value={formik.values?.restaurants || []}
                      onChange={(e) => handleRestaurantChange(e, formik)}
                      error={formik.errors.restaurants && Boolean(formik.touched.restaurants)}
                      touched={formik.touched.restaurants && formik.errors.restaurants}
                    />
                  </Col>
                </Row>

                <Row className="w-100 m-0 mt-4">
                  <Col lg="12">
                    <AppTextField
                      id="numberOfPods"
                      name="numberOfPods"
                      fullWidth
                      isUpperCase={true}
                      type="number"
                      label={`No. of Pods (Number of players: ${eligibleUsersCount || 0} )`}
                      isRequired={true}
                      placeholder="Enter Number of Pods"
                      value={formik.values.numberOfPods}
                      onChange={formik.handleChange}
                      error={formik.touched.numberOfPods && Boolean(formik.errors.numberOfPods)}
                      helperText={formik.touched.numberOfPods && formik.errors.numberOfPods}
                      inputProps={{ min: 1 }}
                    />
                  </Col>
                </Row>

                <Row className="w-100 m-0 mt-4">
                  <Col lg="6">
                    <AppTextField
                      id="prizeTitle"
                      name="prizeTitle"
                      label="Prize Title"
                      isUpperCase={true}
                      fullWidth
                      value={formik.values.prizeTitle}
                      onChange={formik.handleChange}
                      placeholder="Enter Prize Title"
                      error={formik.touched.prizeTitle && formik.errors.prizeTitle}
                      helperText={formik.touched.prizeTitle && formik.errors.prizeTitle}
                    />
                  </Col>
                  <Col lg="6">
                    <ImageUploadField
                      isVariant={true}
                      label="Prize Image"
                      placeholder="Upload Prize Image"
                      value={formik.values.prizeImage}
                      onChange={onImageUpload}
                      onDelete={() => handleImageDelete(formik)}
                      error={formik.touched.prizeImage && formik.errors.prizeImage}
                      touched={formik.touched.prizeImage}
                    />
                  </Col>
                </Row>

                <Row className="w-100 m-0 mt-4">
                  <Col lg="12">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.separatePodsPerRestaurant}
                          onChange={(e) =>
                            formik.setFieldValue("separatePodsPerRestaurant", e.target.checked)
                          }
                          sx={{
                            color: theme.palette.secondary.main,
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body1Regular"
                          sx={{ color: theme.palette.text.primary }}>
                          Create separate leagues for each selected restaurant (Don&apos;t mix
                          restaurants in leagues)
                        </Typography>
                      }
                    />
                  </Col>
                </Row>
              </Box>
            </Box>

            <Box className="mt-5 d-flex justify-content-center">
              <AppButton
                onClick={formik.handleSubmit}
                sx={{
                  height: "48px",
                  padding: "10px 50px"
                }}>
                {isEdit ? "Edit Contest" : "Create Contest"}
              </AppButton>
            </Box>
          </DialogContent>
        </StyledDialog>
      )}
    </Formik>
  )
}

export default AddNewLeagueForm
